import { useState } from "react";
import { type CouponInfo } from "@/shared/server/apis/types/couponResponse";
import { Center, Flex, Radio, RadioGroup, Stack, Text, useToast } from "@chakra-ui/react";
import { CustomText, Wrapper } from "./couponModal.style";
import { Button } from "@/components";
import { usePaymentRoomState } from "@/shared/client";
import styled from "@emotion/styled";
import { usePostCouponCheck } from "@/shared/server";
import { useModal } from "@/contexts";

export const CouponModal = ({ list }: { list: CouponInfo[] }) => {
    // 선택된 쿠폰의 ID를 관리 (기본값 "none"은 적용 안 함)
    const [selectedValue, setSelectedValue] = useState<string>("none");
    const toast = useToast();
    const { closeModal } = useModal();
    const [paymentState, setPaymentState] = usePaymentRoomState();
    const edate = paymentState[0]?.date
        .split("~")[1]
        .trim()
        .replace(/\(.*?\)/, "");
    const { mutate } = usePostCouponCheck();

    const totalPrice = paymentState.reduce((sum, room) => sum + room.discountPrice, 0);
    // 선택된 쿠폰 객체 찾기
    const selectedCoupon = list.find((item) => item.coupon_name === selectedValue);

    const handleApply = () => {
        if (selectedValue === "none") {
            closeModal();
        } else {
            mutate(
                { edate, coupon_name: selectedValue },
                {
                    onSuccess: (res) => {
                        if (res === "failed") {
                            toast({
                                duration: 3000,
                                position: "top",
                                description: "쿠폰 적용에 실패했습니다. 다시 시도해주세요.",
                            });
                        }

                        if (typeof res === "object" && res !== null) {
                            const couponCnt = Number(res.discount_cnt);
                            if (couponCnt === 0) {
                                return toast({
                                    duration: 3000,
                                    position: "top",
                                    description: "소진된 쿠폰입니다.",
                                    title: "쿠폰 적용 실패",
                                    status: "error",
                                });
                            }
                        }
                        const discountValue = res.discount;
                        if (res.discount_name === "percent") {
                            setPaymentState((prev) =>
                                prev.map((item) => ({
                                    ...item,
                                    couponDiscount: (item.discountPrice * discountValue) / 100,
                                    couponName: selectedCoupon?.coupon_name || "",
                                    coupon_idx: res.coupon_idx,
                                })),
                            );
                        }
                        if (res.discount_name === "won") {
                            setPaymentState((prev) =>
                                prev.map((item) => ({
                                    ...item,
                                    couponDiscount: discountValue / paymentState.length,
                                    couponName: selectedCoupon?.coupon_name || "",
                                    coupon_idx: res.coupon_idx,
                                })),
                            );
                        }
                        toast({
                            duration: 3000,
                            position: "top",
                            title:
                                res.discount_name === "percent"
                                    ? `${res.discount}% 할인 적용`
                                    : `${res.discount}원 할인 적용`,
                            status: "success",
                        });
                        closeModal();
                    },
                },
            );
        }
    };

    const calculateDiscount = (totalPrice: number, discount: number) => {
        // % 할인 (2자리)
        if (discount < 100) {
            return Math.floor((totalPrice * discount) / 100);
        }

        // 원 할인 (3자리 이상)
        return discount;
    };
    return (
        <Wrapper>
            <Center mb="20px">
                <CustomText fontSize="24px" fontWeight="bold">
                    쿠폰 적용
                </CustomText>
            </Center>

            <RadioGroup onChange={setSelectedValue} value={selectedValue}>
                <Stack gap="12px">
                    {/* 적용 안 함 옵션 */}
                    <CouponBox isSelected={selectedValue === "none"} onClick={() => setSelectedValue("none")}>
                        <CustomText>적용 안 함</CustomText>
                        <Radio value="none" colorScheme="blue" />
                    </CouponBox>

                    {/* 쿠폰 리스트 루프 */}
                    {list.map((item) => {
                        const isSelected = selectedValue === item.coupon_name;

                        return (
                            <CouponBox
                                key={item.coupon_idx}
                                isSelected={isSelected}
                                onClick={() => setSelectedValue(item.coupon_name)}
                            >
                                <Flex textAlign="left" flexDir="column">
                                    <CustomText fontWeight="600">
                                        {item.coupon_name} -{" "}
                                        {item.discount.toLocaleString().length > 2
                                            ? `${item.discount.toLocaleString()}원`
                                            : `${item.discount}%`}{" "}
                                        쿠폰
                                    </CustomText>
                                    <CustomText fontSize="14px" color="#dd107f">
                                        {calculateDiscount(totalPrice, Number(item.discount)).toLocaleString()}원 할인
                                    </CustomText>
                                    <Text fontSize="12px">선착순 : {item.cnt}장 남음</Text>
                                </Flex>

                                <Radio value={item.coupon_name} colorScheme="blue" />
                            </CouponBox>
                        );
                    })}
                </Stack>
            </RadioGroup>

            <CustomButton onClick={handleApply} color="white">
                {selectedValue === "none"
                    ? "쿠폰 없이 계속하기"
                    : `${calculateDiscount(totalPrice, Number(selectedCoupon?.discount)).toLocaleString()}원 적용하기`}
            </CustomButton>
        </Wrapper>
    );
};

const CustomButton = styled(Button)`
    background: ${({ theme }) => theme.COLOR.pink[200]};
    border: 1px solid ${({ theme }) => theme.COLOR.pink[200]};
    color: ${({ theme }) => theme.COLOR.white};
`;
const CouponBox = styled(Flex)<{ isSelected: boolean }>`
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;

    border: 1px solid ${({ isSelected, theme }) => (isSelected ? theme.COLOR.pink[200] : "#eee")};

    background: ${({ isSelected }) => (isSelected ? "rgba(221,16,127,0.05)" : "white")};

    &:hover {
        border-color: ${({ theme }) => theme.COLOR.pink[200]};
        background: rgba(221, 16, 127, 0.05);
    }
`;
